import { Button, Stack, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { MasterTradeParams } from "app/models/master-trade-params"
import { TradeEntryEntitySearch } from "../trade-entry-creation/trade-entry-counterparties-section/trade-entry-entity-search"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getEntities } from "app/state/ducks/entities/selectors"
import BlackTextInput from "app/views/components/inputs/BlackTextInput"
import { ErrorNotification, SuccessNotification } from "app/views/components/notifications/notification"
import { editCounterpartiesDetails } from "app/state/ducks/master-trades/thunks"
import { EntityParams } from "app/models/entity-params"
import { getEntityTypes } from "app/state/ducks/entity-type/selectors"
import { EntityTypeParams } from "app/models/entity-type-params"
import { getSubTrades } from "app/state/ducks/sub-trades/selectors"
import { getEntityProfiles } from "app/state/ducks/entity-profile/selectors"
import { isFundManagerEntity } from "app/utils/util-functions"
import WarningText from "app/views/components/warning-text/warning-text"

type Params = {
    trade?: MasterTradeParams
    closeModal: () => void
    onHandleSubmit?: any
    withRedirection?: boolean
}

export default function EditCounterpartiesForm({
    trade,
    closeModal,
    onHandleSubmit
}: Params) {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [myEntity, setMyEntity] = useState<EntityParams>()
    const [counterparty, setCounterparty] = useState<EntityParams>()
    const [myEntityEntityType, setMyEntityEntityType] = useState<EntityTypeParams>()
    const [counterpartyEntityType, setCounterpartyEntityType] = useState<EntityTypeParams>()

    const entities = useSelector(getEntities)
    const entityTypes = useSelector(getEntityTypes)
    const subTrades = useSelector(getSubTrades)
    const entityProfiles = useSelector(getEntityProfiles)

    const isAllocated = useMemo(() => {
        return subTrades.some(st => st.masterTradeId.id === trade?.id)
    }, [subTrades])

    const isFMIncluded = useMemo(() => {
        return isFundManagerEntity(trade?.buyerId?.id, entities, entityProfiles)
            || isFundManagerEntity(trade?.sellerId?.id, entities, entityProfiles)
    }, [trade, entities, entityProfiles])

    const form = useForm({
        initialValues: {
            myEntityId: trade
                ? trade.myEntity == "Buy"
                    ? trade?.buyerId?.id ?? ''
                    : trade?.sellerId?.id ?? ''
                : '',
            counterpartyId: trade
                ? trade.myEntity == "Buy"
                    ? trade?.sellerId?.id ?? ''
                    : trade?.buyerId?.id ?? ''
                : '',
        },
        transformValues: (values) => {
            const myEntity = entities.find(e => e.id === values.myEntityId)
            const counterparty = entities.find(e => e.id === values.counterpartyId)

            return {
                buyerId: trade?.myEntity == "Buy"
                    ? {
                        id: values.myEntityId,
                        admin: myEntity?.maker ?? ''
                    }
                    :
                    {
                        id: values.counterpartyId,
                        admin: counterparty?.maker ?? '',
                    },
                sellerId: trade?.myEntity == "Sell"
                    ? {
                        id: values.myEntityId,
                        admin: myEntity?.maker ?? ''
                    }
                    :
                    {
                        id: values.counterpartyId,
                        admin: counterparty?.maker ?? '',
                    },
            }
        }
    })

    useEffect(() => {
        if (!entities || entities.length < 1 || !form.values.myEntityId || form.values.myEntityId === "") {
            return
        }
        const newMyEntity = entities.find(e => e.id === form.values.myEntityId)
        setMyEntity(newMyEntity)
        const entityType = entityTypes.find(et => et.id === newMyEntity?.entityType.id)
        setMyEntityEntityType(entityType)
    }, [entities, form.values.myEntityId])


    useEffect(() => {
        if (!entities || entities.length < 1 || !form.values.counterpartyId || form.values.counterpartyId === "") {
            return
        }
        const newCounterparty = entities.find(e => e.id === form.values.counterpartyId)
        setCounterparty(newCounterparty)
        const entityType = entityTypes.find(et => et.id === newCounterparty?.entityType.id)
        setCounterpartyEntityType(entityType)

    }, [entities, form.values.counterpartyId])

    const onChange = (value?: string | null, isMyEntity?: boolean) => {
        form.setFieldValue(isMyEntity ? 'myEntityId' : 'counterpartyId', value ?? '')
    }

    async function onSubmit(counterpartiesDetails: any) {
        try {
            setIsLoading(true)
            const response: any = await dispatch(editCounterpartiesDetails(counterpartiesDetails, trade))
            if (response.success) {
                SuccessNotification({
                    title: 'Successful Counterparties Details Saved',
                    message: 'You edited an counterparties details for open Trade',
                })
            } else {
                ErrorNotification({
                    title: 'Counterparties details save failed',
                    message: response.payload ?? 'Check data and try again',
                })
            }
            return undefined
        } catch {
            ErrorNotification({
                title: 'Counterparties Details save failed',
                message: 'Check data and try again',
            })
        } finally {
            setIsLoading(false)
        }
    }

    async function submitForm(event: any) {
        event.preventDefault()
        if (!form.validate().hasErrors) {
            const trade = await onSubmit(form.getTransformedValues())
            onHandleSubmit(trade ?? form.values)
        } else {
            ErrorNotification({
                title: 'Form Validation Error',
                message: 'Please fill in required fields',
            });
        }
    }


    return (
        <form onSubmit={submitForm} className="stepper-form">
            <Stack spacing="12px !important">
                <div className="edit-counterparties">
                    <div className="edit-counterparty">
                        <Text className="counterparty-title">MyEntity ({trade?.myEntity === "Buy" ? "Buyer" : "Seller"})</Text>
                        <TradeEntryEntitySearch loading={isLoading} onChange={(e) => onChange(e, true)} value={form.values.myEntityId} disabled={isFMIncluded && isAllocated} excludeEntityId={form.values.counterpartyId} />
                        <BlackTextInput disabled label="MEI" value={myEntity?.mei ?? ""} pt="12px" />
                        <BlackTextInput disabled label="Entity Type" value={myEntityEntityType?.name ?? ''} />
                        <BlackTextInput disabled label="Lendos ID" value={myEntity?.systemId ?? ""} />
                        <BlackTextInput disabled label="Internal ID" value={myEntity?.internalId ?? ""} />
                    </div>
                    <div className="edit-counterparty">
                        <Text className="counterparty-title">Counterparty ({trade?.myEntity === "Buy" ? "Seller" : "Buyer"})</Text>
                        <TradeEntryEntitySearch loading={isLoading} onChange={(e) => onChange(e, false)} value={form.values.counterpartyId} disabled={isFMIncluded && isAllocated} isRight excludeEntityId={form.values.myEntityId} />
                        <BlackTextInput disabled label="MEI" value={counterparty?.mei ?? ""} pt="12px" />
                        <BlackTextInput disabled label="Entity Type" value={counterpartyEntityType?.name ?? ''} />
                        <BlackTextInput disabled label="Lendos ID" value={counterparty?.systemId ?? ""} />
                        <BlackTextInput disabled label="Internal ID" value={counterparty?.internalId ?? ""} />
                    </div>
                </div>
                {isFMIncluded && isAllocated && <WarningText text="Can't be edited since trade involves a Fund Manager and is allocated" />}
                <div className="action-buttons-block">
                    <Button className="cancel-button" onClick={() => closeModal()}>Cancel</Button>

                    <Button
                        type="submit"
                        loading={isLoading}
                        disabled={!form.isValid()}
                        className="delete-button stratch-delete-button"
                    >
                        Save
                    </Button>
                </div>
            </Stack>
        </form>
    )
}
